import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DatePicker } from 'antd';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import CustomSelect from '../custom-select/CustomSelect';
import { apiService } from '../../services/api';
import { formatProxyCreatedTimeMskOnly } from '../../utils/proxyExpiresDisplay';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import '../proxy-create-panel/proxy-create-panel.css';

dayjs.locale('ru');

const normalizeOperatorForCompare = (value) => String(value || '').trim().toLowerCase();

const operatorsMatch = (a, b) => {
  const na = normalizeOperatorForCompare(a);
  const nb = normalizeOperatorForCompare(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
};

const formatKrakenIdsInput = (arr) => (Array.isArray(arr) ? arr : []).join(', ');

const parseKrakenIntListInput = (raw) => {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
  }
  return String(raw).trim().split(/[,;\s]+/).filter(Boolean)
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n));
};

/** Дата окончания YYYY-MM-DD + локальное время из created_at (как при продлении на сутки). */
const buildExpiresAtIsoFromCalendarDayAndProxy = (calendarDayYmd, proxyRecord) => {
  if (!calendarDayYmd) return null;
  const rawCreated = proxyRecord?._original?.created_at ?? proxyRecord?.created_at;
  if (!rawCreated) return `${calendarDayYmd}T23:59:59`;
  const c = new Date(String(rawCreated).replace(' ', 'T'));
  if (Number.isNaN(c.getTime())) return `${calendarDayYmd}T23:59:59`;
  const pad = (n) => String(n).padStart(2, '0');
  return `${calendarDayYmd}T${pad(c.getHours())}:${pad(c.getMinutes())}:${pad(c.getSeconds())}`;
};

/**
 * ProxyEditPanel — панель редактирования прокси
 * Использует те же CSS-классы (pcp__*) что и ProxyCreatePanel
 * для единообразного дизайна.
 *
 * Props:
 *   visible          — показать/скрыть
 *   proxy            — объект editingProxy
 *   onClose          — закрыть
 *   onSubmit         — (values) => ... обновить прокси
 *   submitting       — загрузка
 *   tariffPlans, proxyTypes, availableCities, krakenConnections,
 *   filteredKrakenConnections, availableServers, filteredServers,
 *   availableOperators, osfpList
 *   onCityChange, onConnectionChange
 *   convertKBytesToMbps — (kbytes) => mbps
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== SUB-COMPONENTS ====================

const ToggleRow = ({ label, checked = false, onChange = null }) => (
  <div className="pcp__toggle-row">
    <span className="pcp__toggle-label">{label}</span>
    <button
      type="button"
      className={`pcp__switch ${checked ? 'pcp__switch--on' : 'pcp__switch--off'}`}
      onClick={() => onChange && onChange(!checked)}
      aria-label={label}
      aria-checked={checked}
      role="switch"
      tabIndex={0}
    >
      <span className="pcp__switch-dot" />
    </button>
  </div>
);

// ==================== MAIN COMPONENT ====================

const ProxyEditPanel = ({
  visible = false,
  proxy = null,
  onClose = null,
  onSubmit = null,
  submitting = false,

  tariffPlans = [],
  proxyTypes = [],
  availableCities = [],
  krakenConnections = [],
  filteredKrakenConnections = [],
  availableServers = [],
  filteredServers = [],
  availableOperators = [],
  osfpList = [],

  onCityChange = null,
  onConnectionChange = null,
  onRefreshOsfp = null,
  osfpRefreshLoading = false,
  convertKBytesToMbps = null,
}) => {
  // ==================== FORM STATE ====================
  const [tariffPlanName, setTariffPlanName] = useState('');
  const [slimiter, setSlimiter] = useState('');
  const [rlimiter, setRlimiter] = useState('');
  const [maxconn, setMaxconn] = useState('');
  const [port, setPort] = useState('');
  const [proxyType, setProxyType] = useState('');
  const [expiresDate, setExpiresDate] = useState(''); // YYYY-MM-DD
  const [city, setCity] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [serverId, setServerId] = useState('');
  const [mobileOperator, setMobileOperator] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [autoReplaceEnabled, setAutoReplaceEnabled] = useState(false);
  const [reconnectLinkEnabled, setReconnectLinkEnabled] = useState(false);
  const [isKeepalive, setIsKeepalive] = useState(false);
  const [isCompression, setIsCompression] = useState(false);
  // UDP у SOCKS5 (DF79): udpServer — настоящее значение (строка таблицы, затем живое с сервера); серверу
  // уходит только сдвиг переключателя от него, а живое значение не перебивает уже сдвинутый руками.
  const [isUdpEnabled, setIsUdpEnabled] = useState(false);
  const [udpServer, setUdpServer] = useState(false);
  const udpTouchedRef = useRef(false);
  const [modemReconnectType, setModemReconnectType] = useState('');
  const [modemReconnectInterval, setModemReconnectInterval] = useState('');
  const [modemOsfpId, setModemOsfpId] = useState('');
  const [modemIsOsfp, setModemIsOsfp] = useState(false);
  const [krakenIsAllow, setKrakenIsAllow] = useState(true);
  const [krakenIsPorts, setKrakenIsPorts] = useState(true);
  const [krakenAllowInput, setKrakenAllowInput] = useState('');
  const [krakenTargetportInput, setKrakenTargetportInput] = useState('');
  // Доступ по спискам (D28): krakenServer — что форма прочитала (строка таблицы, затем живое с сервера). Страница
  // сравнивает правку с ним, а не со строкой таблицы: та бывает старой — очищенный список «не менялся» и не уходил.
  const [krakenServer, setKrakenServer] = useState({});

  const applyKrakenFromPayload = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    const read = {};
    if (Object.prototype.hasOwnProperty.call(data, 'is_allow')) {
      read.is_allow = !!data.is_allow;
      setKrakenIsAllow(read.is_allow);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'is_ports')) {
      read.is_ports = !!data.is_ports;
      setKrakenIsPorts(read.is_ports);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'allow')) {
      const shown = formatKrakenIdsInput(data.allow);
      read.allow = parseKrakenIntListInput(shown);
      setKrakenAllowInput(shown);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'targetport')) {
      const shown = formatKrakenIdsInput(data.targetport);
      read.targetport = parseKrakenIntListInput(shown);
      setKrakenTargetportInput(shown);
    }
    setKrakenServer((prev) => ({ ...prev, ...read }));
    // Midori отдаёт UDP только так (Kraken — никак): это и есть точка отсчёта переключателя.
    if (typeof data.is_udp === 'boolean') {
      setUdpServer(data.is_udp);
      if (!udpTouchedRef.current) setIsUdpEnabled(data.is_udp);
    }
  }, []);

  const handleUdpChange = useCallback((value) => {
    udpTouchedRef.current = true;
    setIsUdpEnabled(value);
  }, []);

  // ==================== INIT FROM PROXY ON OPEN ====================
  useEffect(() => {
    if (!visible || !proxy) return;

    setTariffPlanName(proxy.tariff_plan || '');

    // Convert slimiter from KBytes/s to Mbps
    if (proxy.slimiter && proxy.slimiter > 0 && convertKBytesToMbps) {
      setSlimiter(String(convertKBytesToMbps(proxy.slimiter)));
    } else {
      setSlimiter('');
    }

    setRlimiter(proxy.rlimiter && proxy.rlimiter > 0 ? String(proxy.rlimiter) : '');
    setMaxconn(proxy.maxconn && proxy.maxconn > 0 ? String(proxy.maxconn) : '');
    setPort(proxy.port ? String(proxy.port) : '');
    setProxyType(proxy.proxy_type || proxyTypes[0] || 'http');

    const proxyExpires = proxy._original?.expires_at ?? proxy.expires_at;
    if (proxyExpires) {
      const normalized = String(proxyExpires).replace(' ', 'T');
      const d = new Date(normalized);
      if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        setExpiresDate(`${yyyy}-${mm}-${dd}`);
      } else {
        setExpiresDate('');
      }
    } else {
      setExpiresDate('');
    }
    setCity(proxy.selected_city || proxy.city || '');
    const initialConnectionId = proxy.connection_id ?? proxy.kraken_connection_id ?? '';
    setConnectionId(initialConnectionId ? String(initialConnectionId) : '');
    setServerId(proxy.server_id ? String(proxy.server_id) : '');
    setMobileOperator(proxy.selected_operator || proxy.mobile_operator || '');
    setIsActive(proxy.is_active !== undefined ? !!proxy.is_active : true);
    setAutoReplaceEnabled(!!proxy.auto_replace_enabled);
    setReconnectLinkEnabled(!!proxy.reconnect_link_enabled);
    setIsKeepalive(!!proxy.is_keepalive);
    setIsCompression(!!proxy.is_compression);
    udpTouchedRef.current = false;
    setIsUdpEnabled(!!proxy.is_udp_enabled);
    setUdpServer(!!proxy.is_udp_enabled);
    setModemReconnectType(proxy.modem_reconnect_type || '');
    setModemReconnectInterval(proxy.modem_reconnect_interval != null ? String(proxy.modem_reconnect_interval) : '');
    setModemOsfpId(proxy.modem_osfp_id != null ? String(proxy.modem_osfp_id) : '');
    setModemIsOsfp(!!proxy.modem_is_osfp);
    setKrakenServer({});
    applyKrakenFromPayload(proxy.kraken_characteristics);
  }, [visible, proxy, proxyTypes, convertKBytesToMbps, applyKrakenFromPayload]);

  useEffect(() => {
    if (!visible || !proxy) return;
    const dbId = proxy.proxy_id ?? proxy.id;
    if (!dbId || !proxy.kraken_proxy_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getProxyCharacteristics(dbId);
        if (!cancelled && res?.data) {
          applyKrakenFromPayload(res.data);
        }
      } catch {
        /* оставляем данные из kraken_characteristics строки таблицы */
      }
    })();
    return () => { cancelled = true; };
  }, [visible, proxy, applyKrakenFromPayload]);

  // ==================== CASCADING ====================
  const handleCityChange = useCallback((val) => {
    setCity(val);
    setConnectionId('');
    setServerId('');
    if (onCityChange) onCityChange(val);
  }, [onCityChange]);

  const handleConnectionChange = useCallback((val) => {
    setConnectionId(val);
    setServerId('');
    setMobileOperator('');
    if (onConnectionChange) onConnectionChange(val);
  }, [onConnectionChange]);

  const handleOperatorChange = useCallback((val) => {
    setMobileOperator(val);
    const effectiveServers = connectionId ? filteredServers : availableServers;
    const currentServer = effectiveServers.find((s) => String(s.id) === String(serverId));
    const currentServerMatches = currentServer
      ? operatorsMatch(currentServer.mobile_operator, val)
      : false;
    if (serverId && !currentServerMatches) {
      setServerId('');
    }
  }, [connectionId, serverId, filteredServers, availableServers]);

  // ==================== SUBMIT ====================
  const handleSubmit = useCallback(() => {
    if (!onSubmit) return;
    const currentTariff = tariffPlans.find((t) => t.name === tariffPlanName);
    const modemEnabled = !!(currentTariff && currentTariff.modem_settings_enabled);

    const values = {
      tariff_plan_name: tariffPlanName || undefined,
      slimiter: slimiter !== '' ? Number(slimiter) : undefined,
      rlimiter: rlimiter !== '' ? Number(rlimiter) : undefined,
      maxconn: maxconn !== '' ? Number(maxconn) : undefined,
      port: port !== '' ? Number(port) : undefined,
      proxy_type: proxyType || undefined,
      city: city || undefined,
      connection_id: connectionId || undefined,
      server_id: serverId || undefined,
      mobile_operator: mobileOperator || undefined,
      is_active: isActive,
      auto_replace_enabled: autoReplaceEnabled,
      reconnect_link_enabled: reconnectLinkEnabled,
      is_keepalive: isKeepalive,
      is_compression: isCompression,
      ...(isUdpEnabled !== udpServer ? { is_udp_enabled: isUdpEnabled } : {}),
      kraken_is_allow: krakenIsAllow,
      kraken_is_ports: krakenIsPorts,
      kraken_allow: parseKrakenIntListInput(krakenAllowInput),
      kraken_targetport: parseKrakenIntListInput(krakenTargetportInput),
      kraken_server: krakenServer,
    };

    if (modemEnabled) {
      values.modem_reconnect_type = modemReconnectType || undefined;
      if (modemReconnectType === 'link') {
        values.modem_reconnect_interval = null;
      } else {
        values.modem_reconnect_interval = modemReconnectInterval !== '' ? Number(modemReconnectInterval) : undefined;
      }
      values.modem_osfp_id = modemOsfpId || undefined;
      values.modem_is_osfp = modemIsOsfp;
    }

    if (expiresDate) {
      values.expires_at = buildExpiresAtIsoFromCalendarDayAndProxy(expiresDate, proxy);
    } else {
      values.expires_at = null; // Безлимит — явно сбрасываем срок
    }

    onSubmit(values);
  }, [
    tariffPlanName, slimiter, rlimiter, maxconn, port, proxyType,
    expiresDate,
    city, connectionId, serverId, mobileOperator,
    isActive, autoReplaceEnabled, reconnectLinkEnabled, isKeepalive, isCompression, isUdpEnabled, udpServer,
    krakenIsAllow, krakenIsPorts, krakenAllowInput, krakenTargetportInput, krakenServer,
    modemReconnectType, modemReconnectInterval, modemOsfpId, modemIsOsfp,
    tariffPlans, onSubmit, proxy,
  ]);

  // ==================== CLOSE ====================
  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  // ==================== DERIVED LISTS ====================
  const connectionsToShow = city ? filteredKrakenConnections : krakenConnections;
  const serversToShow = connectionId ? filteredServers : availableServers;

  const selectedTariff = tariffPlans.find((t) => t.name === tariffPlanName);
  const showModemSettings = !!(selectedTariff && selectedTariff.modem_settings_enabled);

  // Затемнение закрывает форму только настоящим щелчком — нажатие и отпускание на нём (просьба владельца 28.10)
  const backdrop = useBackdropClose(handleClose);

  // ==================== EARLY RETURN ====================
  if (!visible || !proxy) return null;

  return createPortal(
    <div
      className="pcp-overlay"
      {...backdrop}
    >
      <div className="pcp" role="dialog" aria-label="Редактирование прокси">

        {/* ==================== HEADER ==================== */}
        <div className="pcp__header">
          <h2 className="pcp__title">редактирование прокси</h2>
          <button
            type="button"
            className="pcp__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ==================== BODY ==================== */}
        <div className="pcp__body">

          {/* ---- Section: Информация о прокси (read-only) ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Информация о прокси</h3>
            <div className="pcp__info-box" style={{ background: '#ecf1fe' }}>
              <p className="pcp__info-box-text" style={{ color: '#5283f4' }}>
                ID: {proxy.proxy_id} &nbsp;|&nbsp; Клиент: {proxy.client_login} &nbsp;|&nbsp; IP: {proxy.server_ip} &nbsp;|&nbsp; Сервер: {proxy.server_name || 'Неизвестно'}:{proxy.port}
              </p>
            </div>
          </div>

          {/* ---- Section: Тариф и лимиты ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Тариф и лимиты</h3>

            {/* Тарифный план */}
            <div className="pcp__field">
              <div className="pcp__label">
                <span className="pcp__label-text">Тарифный план</span>
              </div>
              <CustomSelect
                value={tariffPlanName}
                onChange={setTariffPlanName}
                placeholder="Выберите тарифный план"
                options={[
                  { value: '', label: 'Выберите тарифный план' },
                  ...tariffPlans.map((plan) => ({ value: plan.name, label: plan.name })),
                ]}
                searchable
              />
            </div>

            {/* Порт / Тип / Срок */}
            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Номер порта</span>
                </div>
                <input
                  type="number"
                  className="pcp__text-input"
                  placeholder="8080"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  min={1}
                  max={65535}
                />
              </div>
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Тип прокси</span>
                </div>
                <CustomSelect
                  value={proxyType}
                  onChange={setProxyType}
                  placeholder="Тип прокси"
                  options={proxyTypes.map((t) => ({ value: t, label: t.toUpperCase() }))}
                />
              </div>
              <div className="pcp__field pcp__field--full-mobile pcp__field--expires">
                <div className="pcp__label">
                  <span className="pcp__label-text">Срок действия</span>
                </div>
                <div className="pcp__expires-wrap">
                  <DatePicker
                    value={expiresDate ? dayjs(expiresDate) : null}
                    onChange={(date) => {
                      if (date) {
                        const iso = date.format('YYYY-MM-DD');
                        setExpiresDate(iso);
                      } else {
                        setExpiresDate('');
                      }
                    }}
                    format="DD.MM.YYYY"
                    placeholder="Безлимит"
                    style={{ width: '100%' }}
                    allowClear
                    getPopupContainer={(trigger) => trigger.parentElement}
                    renderExtraFooter={() => (
                      <div className="pcp__expires-footer">
                        {expiresDate && (() => {
                          const now = dayjs().startOf('day');
                          const exp = dayjs(expiresDate).startOf('day');
                          const days = exp.diff(now, 'day');
                          const isExpired = days < 0;
                          const isSoon = days >= 0 && days <= 3;
                          let label = '';
                          if (days < 0) label = `Просрочено на ${Math.abs(days)} дн.`;
                          else if (days === 0) label = 'Истекает сегодня';
                          else if (days === 1) label = 'Остался 1 день';
                          else if (days >= 2 && days <= 4) label = `Осталось ${days} дня`;
                          else label = `Осталось ${days} дней`;
                          return (
                            <span className={`pcp__expires-days-hint ${isExpired ? 'pcp__expires-days-hint--expired' : ''} ${isSoon ? 'pcp__expires-days-hint--soon' : ''}`}>
                              {label}
                            </span>
                          );
                        })()}
                        <div className="pcp__expires-presets">
                          <button
                            type="button"
                            className={`pcp__expires-preset-btn ${!expiresDate ? 'pcp__expires-preset-btn--active' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setExpiresDate('')}
                          >
                            Безлимит
                          </button>
                          {[30, 60, 90, 180, 365].map((d) => {
                            const target = dayjs().add(d, 'day');
                            const iso = target.format('YYYY-MM-DD');
                            return (
                              <button
                                key={d}
                                type="button"
                                className={`pcp__expires-preset-btn ${expiresDate === iso ? 'pcp__expires-preset-btn--active' : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setExpiresDate(iso);
                                }}
                              >
                                +{d}д
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  />
                </div>
                {expiresDate &&
                  (() => {
                    const anchor = formatProxyCreatedTimeMskOnly(
                      proxy?._original?.created_at ?? proxy?.created_at
                    );
                    if (!anchor) return null;
                    return (
                      <p
                        className="pcp__expires-time-anchor-hint"
                        style={{ fontSize: 12, marginTop: 6, color: '#5c6470', lineHeight: 1.35 }}
                      >
                        Окончание в выбранный день — в <strong>{anchor} МСК</strong> (как при создании прокси).
                      </p>
                    );
                  })()}
              </div>
            </div>
          </div>

          {/* ---- Section: Размещение ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Размещение</h3>

            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Город</span>
                </div>
                <CustomSelect
                  value={city}
                  onChange={handleCityChange}
                  placeholder="Выберите город"
                  options={[
                    { value: '', label: 'Выберите город' },
                    ...availableCities.map((c) => ({ value: c, label: c })),
                  ]}
                  searchable
                />
              </div>
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Сервер Kraken</span>
                </div>
                <CustomSelect
                  value={connectionId}
                  onChange={handleConnectionChange}
                  placeholder={`Выберите сервер (${connectionsToShow.length})`}
                  options={[
                    { value: '', label: `Выберите сервер (${connectionsToShow.length})` },
                    ...connectionsToShow.map((conn) => ({ value: String(conn.id), label: `${conn.name} (${conn.server_url})` })),
                  ]}
                  searchable
                />
              </div>
            </div>

            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Модем</span>
                </div>
                <CustomSelect
                  value={serverId}
                  onChange={setServerId}
                  placeholder={`Выберите модем (${serversToShow.length})`}
                  options={[
                    { value: '', label: `Выберите модем (${serversToShow.length})` },
                    ...serversToShow.map((s) => { const octet = (s.ip_address || s.ip || '').split('.')[2]; return { value: String(s.id), label: octet ? `№ ${octet} ${s.name || s.ip}` : (s.name || s.ip) }; }),
                  ]}
                  searchable
                />
              </div>
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Оператор</span>
                </div>
                <CustomSelect
                  value={mobileOperator}
                  onChange={handleOperatorChange}
                  placeholder="Выберите оператора"
                  options={[
                    { value: '', label: 'Выберите оператора' },
                    ...availableOperators.map((op) => ({ value: op, label: op })),
                  ]}
                />
              </div>
            </div>
          </div>

          {/* ---- Section: Параметры ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Параметры</h3>
            <ToggleRow label={isActive ? 'Прокси активна' : 'Прокси неактивна'} checked={isActive} onChange={setIsActive} />
            <ToggleRow label="Автозамена устройства при недоступности прокси" checked={autoReplaceEnabled} onChange={setAutoReplaceEnabled} />
            <ToggleRow label="Ссылка смены IP" checked={reconnectLinkEnabled} onChange={setReconnectLinkEnabled} />
            {proxyType !== 'socks5' && (
              <>
                <ToggleRow label="HTTP Keep-Alive соединения" checked={isKeepalive} onChange={setIsKeepalive} />
                <ToggleRow label="Сжатие HTTP-ответов (gzip)" checked={isCompression} onChange={setIsCompression} />
              </>
            )}
            {proxyType === 'socks5' && (
              <ToggleRow label="UDP для SOCKS5" checked={isUdpEnabled} onChange={handleUdpChange} />
            )}
          </div>

          {/* ---- Section: Разрешения Kraken ---- */}
          <div className="pcp__section">
            <h3 className="pcp__section-title">Разрешения прокси</h3>
            <ToggleRow
              label="Доступ прокси по списку ресурсов"
              checked={krakenIsAllow}
              onChange={setKrakenIsAllow}
            />
            <ToggleRow
              label="Доступ к портам из списка"
              checked={krakenIsPorts}
              onChange={setKrakenIsPorts}
            />
            <div className="pcp__field">
              <div className="pcp__label">
                <span className="pcp__label-text">Разрешенные ресурсы по ID</span>
              </div>
              <input
                type="text"
                className="pcp__text-input"
                placeholder="например: 1, 2, 5"
                value={krakenAllowInput}
                onChange={(e) => setKrakenAllowInput(e.target.value)}
                aria-label="Разрешенные ресурсы по ID, через запятую"
                autoComplete="off"
              />
            </div>
            <div className="pcp__field">
              <div className="pcp__label">
                <span className="pcp__label-text">Разрешенные порты</span>
              </div>
              <input
                type="text"
                className="pcp__text-input"
                placeholder="например: 80, 443"
                value={krakenTargetportInput}
                onChange={(e) => setKrakenTargetportInput(e.target.value)}
                aria-label="Разрешенные порты, через запятую"
                autoComplete="off"
              />
            </div>
          </div>

          {/* ---- Section: Настройки модема (только если тариф поддерживает) ---- */}
          {showModemSettings && (
            <div className="pcp__section">
              <h3 className="pcp__section-title">Настройки модема</h3>
              <div className="pcp__info-box" style={{ background: '#ecf1fe' }}>
                <p className="pcp__info-box-text" style={{ color: '#5283f4' }}>
                  Сохраняются в прокси и применяются при переподключении/автозамене
                </p>
              </div>

              <div className="pcp__row">
                <div className="pcp__field">
                  <div className="pcp__label">
                    <span className="pcp__label-text">Тип переподключения</span>
                  </div>
                  <CustomSelect
                    value={modemReconnectType}
                    onChange={setModemReconnectType}
                    placeholder="Выберите тип"
                    options={[
                      { value: '', label: 'Выберите тип' },
                      { value: 'time', label: 'По времени' },
                      { value: 'link', label: 'Ссылка' },
                      { value: 'time_link', label: 'Время + ссылка' },
                    ]}
                  />
                </div>
                {modemReconnectType !== 'link' && (
                  <div className="pcp__field">
                    <div className="pcp__label">
                      <span className="pcp__label-text">Интервал (сек)</span>
                    </div>
                    <input
                      type="number"
                      className="pcp__text-input"
                      placeholder="240"
                      value={modemReconnectInterval}
                      onChange={(e) => setModemReconnectInterval(e.target.value)}
                      min={1}
                      max={86400}
                    />
                  </div>
                )}
              </div>

              <div className="pcp__row">
                <div className="pcp__field">
                  <div className="pcp__label">
                    <span className="pcp__label-text">TCP отпечаток</span>
                    <button
                      type="button"
                      className="pcp__label-action"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onRefreshOsfp) onRefreshOsfp();
                      }}
                      disabled={osfpRefreshLoading}
                      aria-label="Обновить TCP отпечаток"
                      tabIndex={0}
                    >
                      {osfpRefreshLoading ? 'Обновление...' : 'Обновить'}
                    </button>
                  </div>
                  <CustomSelect
                    value={modemOsfpId}
                    onChange={(val) => {
                      setModemOsfpId(val);
                      if (val) setModemIsOsfp(true);
                    }}
                    placeholder="Выберите отпечаток"
                    options={[
                      { value: '', label: 'Выберите отпечаток' },
                      ...osfpList.map((o) => ({ value: String(o.id), label: o.name || `ID: ${o.id}` })),
                    ]}
                  />
                </div>
                <div className="pcp__field">
                  <ToggleRow label="Включить TCP отпечаток" checked={modemIsOsfp} onChange={setModemIsOsfp} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ==================== FOOTER ==================== */}
        <div className="pcp__footer">
          <button
            type="button"
            className="pcp__btn pcp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Отмена"
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="pcp__btn pcp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-label="Сохранить"
            tabIndex={0}
          >
            {submitting ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProxyEditPanel;
